import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import AdminLayout from '../../components/admin/AdminLayout';
import { Check, X, Loader2, ArrowLeft, Download, Key } from 'lucide-react';

interface AccessRequest {
  id: string;
  first_name: string;
  last_name: string;
  title: string;
  company: string;
  country_region: string;
  phone: string;
  email: string;
  product_type: 'perplexity_meet' | 'reports_detective';
  plan_amount: number;
  user_seats: number;
  trial_days: number;
  created_at: string;
  status: string;
}

export default function Access() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<AccessRequest | null>(null);
  const [yearFilter, setYearFilter] = useState<string>('all');

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    if (yearFilter === 'all') {
      setFilteredRequests(requests);
    } else {
      const targetYear = parseInt(yearFilter);
      const filtered = requests.filter(req => {
        const date = new Date(req.created_at);
        const year = date.getUTCFullYear();
        return year === targetYear;
      });
      setFilteredRequests(filtered);
    }
  }, [requests, yearFilter]);

  const fetchRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('access_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRequests(data || []);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      const { error } = await supabase
        .from('access_requests')
        .update({ status })
        .eq('id', id);

      if (error) throw error;
      
      setRequests(requests.map(request => 
        request.id === id ? { ...request, status } : request
      ));
    } catch (error: any) {
      setError(error.message);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const downloadCSV = () => {
    const headers = [
      'Date',
      'First Name',
      'Last Name',
      'Title',
      'Company',
      'Country/Region',
      'Phone',
      'Email',
      'Product Type',
      'Plan Amount',
      'User Seats',
      'Trial Days',
      'Status'
    ];

    const csvContent = [
      headers.join(','),
      ...filteredRequests.map(request => [
        formatDate(request.created_at),
        request.first_name,
        request.last_name,
        request.title,
        request.company,
        request.country_region,
        request.phone,
        request.email,
        request.product_type,
        request.plan_amount,
        request.user_seats,
        request.trial_days,
        request.status
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const yearSuffix = yearFilter === 'all' ? '' : `-${yearFilter}`;
    link.download = `access-requests${yearSuffix}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Dashboard
              </button>
              <h1 className="text-3xl font-bold text-gray-900">Access Requests</h1>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Filter by Year:</label>
                <select
                  value={yearFilter}
                  onChange={(e) => setYearFilter(e.target.value)}
                  className="bg-white border border-gray-300 text-gray-900 px-3 py-2 rounded-lg focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent"
                >
                  <option value="all">All Years</option>
                  <option value="2025">2025</option>
                  <option value="2026">2026</option>
                </select>
              </div>

              {requests.length > 0 && (
                <button
                  onClick={downloadCSV}
                  className="flex items-center gap-2 bg-primary bg-opacity-10 text-black font-bold px-4 py-2 rounded-lg hover:bg-opacity-20 transition-colors"
                  title="Download requests as CSV"
                >
                  <Download className="w-5 h-5 text-primary" />
                  Export CSV
                </button>
              )}
            </div>
          </div>

          {!loading && requests.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="text-primary text-2xl font-bold">{requests.length}</div>
                <div className="text-gray-600 text-sm">Total Requests</div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="text-primary text-2xl font-bold">
                  {requests.filter(r => new Date(r.created_at).getUTCFullYear() === 2025).length}
                </div>
                <div className="text-gray-600 text-sm">2025 Requests</div>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="text-primary text-2xl font-bold">
                  {requests.filter(r => new Date(r.created_at).getUTCFullYear() === 2026).length}
                </div>
                <div className="text-gray-600 text-sm">2026 Requests</div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-500 p-4 rounded-lg mb-6">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : requests.length === 0 ? (
            <div className="bg-white rounded-lg p-6 text-center text-gray-600">
              No access requests yet.
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="bg-white rounded-lg p-6 text-center text-gray-600">
              No access requests found for the selected year.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredRequests.map((request) => (
                        <tr 
                          key={request.id} 
                          className={`hover:bg-gray-50 cursor-pointer transition-colors ${selectedRequest?.id === request.id ? 'bg-[#40E0D0]/5' : ''}`}
                          onClick={() => setSelectedRequest(request)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDate(request.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {request.first_name} {request.last_name}
                            </div>
                            <div className="text-sm text-gray-600">{request.title}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{request.company}</div>
                            <div className="text-sm text-gray-600">{request.country_region}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {request.product_type === 'perplexity_meet' ? 'Perplexity Meet' : 'Reports Detective'}
                            </div>
                            <div className="text-sm text-gray-600">
                              {request.user_seats} seats / ${request.plan_amount}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              request.status === 'completed' 
                                ? 'bg-green-100 text-green-800' 
                                : request.status === 'in_progress'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {request.status === 'completed' 
                                ? 'Completed' 
                                : request.status === 'in_progress'
                                ? 'In Progress'
                                : 'New'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <div className="flex space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateStatus(request.id, 'in_progress');
                                }}
                                className="text-yellow-500 hover:text-yellow-400"
                                title="Mark as In Progress"
                              >
                                <Loader2 className="w-5 h-5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateStatus(request.id, 'completed');
                                }}
                                className="text-green-500 hover:text-green-400"
                                title="Mark as Completed"
                              >
                                <Check className="w-5 h-5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateStatus(request.id, 'new');
                                }}
                                className="text-gray-500 hover:text-gray-600"
                                title="Mark as New"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Key className="w-5 h-5 text-primary" />
                  <h2 className="text-xl font-semibold text-gray-900">Request Details</h2>
                </div>
                
                {selectedRequest ? (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-600">From</h3>
                      <p className="text-gray-900">{selectedRequest.first_name} {selectedRequest.last_name}</p>
                      <p className="text-gray-600">{selectedRequest.title} at {selectedRequest.company}</p>
                    </div>

                    <div>
                      <h3 className="text-sm font-medium text-gray-600">Contact Information</h3>
                      <p className="text-gray-900">{selectedRequest.email}</p>
                      <p className="text-gray-900">{selectedRequest.phone}</p>
                    </div>

                    <div>
                      <h3 className="text-sm font-medium text-gray-600">Product Details</h3>
                      <p className="text-gray-900">
                        {selectedRequest.product_type === 'perplexity_meet' ? 'Perplexity Meet' : 'Reports Detective'}
                      </p>
                      <p className="text-gray-900">Plan Amount: ${selectedRequest.plan_amount}</p>
                      <p className="text-gray-900">User Seats: {selectedRequest.user_seats}</p>
                      <p className="text-gray-900">Trial Period: {selectedRequest.trial_days} days</p>
                    </div>

                    <div>
                      <h3 className="text-sm font-medium text-gray-600">Additional Details</h3>
                      <p className="text-gray-900">Location: {selectedRequest.country_region}</p>
                      <p className="text-gray-900">Submitted: {formatDate(selectedRequest.created_at)}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-600 py-8">
                    Select a request to view details
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}